/**
 * « Revenue over time per platform » — CA TTC jour par jour, empilé par canal
 * (Shopify, Stripe, Amazon, BtoB).
 *
 * Aires EMPILÉES : chaque bande est la part d'un canal, le haut de la pile est
 * le CA total du jour — on lit d'un coup le total et qui le porte. Mêmes
 * couleurs que le camembert « Par canal » du Résumé, fixées par canal.
 * Un point sur chaque jour (≤ 62 jours), un fin liseré de surface entre les
 * bandes pour que deux aires voisines ne se fondent pas.
 *
 * CA net (remboursements déduits) : un jour où les remboursements dépassent
 * les ventes d'un canal descend sous zéro — l'empilement le montre tel quel.
 * Par semaine au-delà de deux mois, par tranche de 30 jours au-delà d'un an.
 */

import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatDayLong, formatDayShort, formatMoney, formatMoneyCompact } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

export interface PlatformSeries {
  key: string;
  label: string;
  color: string;
  /** CA TTC par jour `YYYY-MM-DD`. Un jour absent vaut zéro. */
  byDay: ReadonlyMap<string, number>;
}

type Row = { date: string; total: number } & Record<string, number | string>;

/** Marqueurs « ventes » : impact lu sur le CA total. */
const MARKER_CONFIG = {
  value: (row: Row) => row.total,
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'sales',
} as const;

function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

function PlatformTooltip({
  active,
  payload,
  series,
  bucketSize,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload?: Row }>;
  series: readonly PlatformSeries[];
  bucketSize: number;
  currency: string;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{bucketLabel(row.date, bucketSize)}</p>
      {/* Du haut de la pile vers le bas, comme on lit le graphique. */}
      {[...series].reverse().map((s) => (
        <div className="tooltip__row" key={s.key}>
          <span className="legend__key legend__key--area" style={{ background: s.color }} aria-hidden="true" />
          <span className="tooltip__name">{s.label}</span>
          <span className="tooltip__value">{formatMoney(Number(row[s.key] ?? 0), currency)}</span>
        </div>
      ))}
      <p className="tooltip__title">Total : {formatMoney(row.total, currency)}</p>
    </div>
  );
}

export function PlatformRevenueChart({
  dates,
  series,
  currency,
}: {
  /** Tous les jours de la période, dans l'ordre. */
  dates: readonly string[];
  /** De bas en haut de la pile. */
  series: readonly PlatformSeries[];
  currency: string;
}) {
  const bucketSize = bucketSizeFor(dates.length);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (let start = 0; start < dates.length; start += bucketSize) {
      const slice = dates.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      const row: Row = { date: first, total: 0 };
      let total = 0;
      for (const s of series) {
        const sum = slice.reduce((acc, date) => acc + (s.byDay.get(date) ?? 0), 0);
        row[s.key] = Math.round(sum * 100) / 100;
        total += sum;
      }
      row.total = Math.round(total * 100) / 100;
      out.push(row);
    }
    return out;
  }, [dates, series, bucketSize]);

  const markers = useChartMarkers(rows, MARKER_CONFIG);

  const columns: ReadonlyArray<DataColumn<Row>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'À partir du', render: (row) => formatDayLong(row.date) },
    ...series.map((s) => ({
      key: s.key,
      header: s.label,
      numeric: true,
      render: (row: Row) => formatMoney(Number(row[s.key] ?? 0), currency),
    })),
    { key: 'total', header: 'Total TTC', numeric: true, render: (row) => formatMoney(row.total, currency) },
  ];

  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const granularity = bucketSize === 1 ? 'par jour' : bucketSize === 7 ? 'par semaine' : 'par tranche de 30 jours';
  const hasData = rows.some((row) => row.total !== 0);

  return (
    <ChartCard
      footer={<MarkerStrip items={markers.items} />}
      title="Revenue over time per platform (TTC)"
      subtitle={`CA TTC net empilé par canal, ${granularity} — le haut de la pile est le CA total`}
      className="chart-grid__wide"
      rows={hasData ? rows : []}
      columns={columns}
      emptyMessage="Aucune vente sur cette période."
      chartHeight={360}
    >
      <ChartLegend entries={series.map((s) => ({ label: s.label, color: s.color, shape: 'area' as const }))} />
      <ResponsiveContainer width="100%" height="86%">
        <AreaChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
          <YAxis tickFormatter={(value: number) => formatMoneyCompact(value, currency)} tick={axisTick} axisLine={false} tickLine={false} width={60} />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={<PlatformTooltip series={series} bucketSize={bucketSize} currency={currency} />}
          />
          {markers.lines}
          {series.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stackId="revenue"
              stroke={s.color}
              strokeWidth={LINE_WIDTH}
              fill={s.color}
              fillOpacity={0.3}
              dot={rows.length <= 62 ? { r: 4, fill: s.color, stroke: COLORS.surface, strokeWidth: 2 } : false}
              activeDot={{ r: 5, fill: s.color, stroke: COLORS.surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
